import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import SmartCMAWorkflow from 'components/smart-cma-workflow/SmartCMAWorkflow';
import axiosInstance from 'helpers/api';
import './SellerValuationWrapper.css';

interface SellerValuationWrapperProps {
  mode?: 'consumer' | 'agent';
}

interface AgentInfo {
  name: string;
  photo?: string;
  email: string;
  phone: string;
  brokerage?: string;
}

const SellerValuationWrapper: React.FC<SellerValuationWrapperProps> = ({ mode = 'consumer' }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Extract parameters from Agent K's personalized link
  const leadId = searchParams.get('lid');
  const agentId = searchParams.get('aid');
  const trackingToken = searchParams.get('t');
  const encodedAddress = searchParams.get('address');
  const reportId = searchParams.get('report_id');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentInfo, setAgentInfo] = useState<AgentInfo | null>(null);
  const [valuationComplete, setValuationComplete] = useState(false);
  const [captureInfo, setCaptureInfo] = useState({
    name: '',
    email: '',
    phone: '',
    timeline: 'Just exploring'
  });
  
  // Track page visit
  useEffect(() => {
    trackEngagement('page_view');
    loadInitialData();
  }, []);

  const trackEngagement = async (action: string, metadata: any = {}) => {
    try {
      await axiosInstance.post('/api/platform-activity', {
        lead_id: leadId,
        activity_type: action,
        platform: 'chat.realestate',
        session_id: trackingToken,
        metadata: {
          page: 'seller-valuation',
          agent_id: agentId,
          ...metadata
        }
      });
    } catch (error) {
      console.error('Failed to track engagement:', error);
    }
  };

  const loadInitialData = async () => {
    try {
      // Validate tracking token
      if (trackingToken) {
        const validationResponse = await axiosInstance.get(`/api/tracking/validate/${trackingToken}`);
        if (!validationResponse.data.valid) {
          setError('This link has expired. Please contact your agent for a new valuation.');
          setLoading(false);
          return;
        }
      }

      // Load agent information
      if (agentId) {
        const agentResponse = await axiosInstance.get(`/api/agents/${agentId}/public-info`);
        setAgentInfo(agentResponse.data);
      }

      // Pre-fill property data if available
      if (encodedAddress) {
        const address = decodeURIComponent(encodedAddress);
        // Store in session for SmartCMAWorkflow to pick up
        sessionStorage.setItem('valuationPreFill', JSON.stringify({
          property_address: address,
          source: 'agent_k_invitation',
          lead_id: leadId,
          report_id: reportId
        }));
      }

      setLoading(false);
    } catch (error) {
      console.error('Failed to load initial data:', error);
      setError('Failed to load valuation data. Please try again.');
      setLoading(false);
    }
  };

  const handleValuationComplete = async (data: any) => {
    trackEngagement('valuation_viewed', {
      valuation_id: data.valuation_id,
      estimated_value: data.estimated_value,
      confidence_score: data.confidence_score
    });
    
    setValuationComplete(true);
    
    // Store valuation data for lead capture
    sessionStorage.setItem('completedValuation', JSON.stringify(data));
  };

  const handleLeadCapture = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // If already a lead, just update engagement
      if (leadId) {
        await axiosInstance.post('/api/leads/update-engagement', {
          lead_id: leadId,
          engagement_data: {
            viewed_valuation: true,
            timeline: captureInfo.timeline,
            platform: 'chat.realestate',
            source: 'seller_valuation'
          }
        });
        
        trackEngagement('lead_updated', { timeline: captureInfo.timeline });
        
        // Redirect to agent contact
        navigate(`/contact-agent/${agentId}`);
      } else {
        // New lead capture
        const valuationData = JSON.parse(sessionStorage.getItem('completedValuation') || '{}');
        
        const response = await axiosInstance.post('/api/leads/capture-seller', {
          ...captureInfo,
          agent_id: agentId,
          property_address: decodeURIComponent(encodedAddress || ''),
          valuation_data: valuationData,
          source: 'chat.realestate',
          entry_point: 'seller_valuation'
        });
        
        trackEngagement('lead_captured', { 
          lead_id: response.data.lead_id,
          timeline: captureInfo.timeline 
        });
        
        // Show success message and next steps
        navigate('/seller-success', { 
          state: { 
            leadId: response.data.lead_id,
            agentInfo,
            valuationData 
          } 
        });
      }
    } catch (error) {
      console.error('Failed to capture lead:', error);
      setError('Failed to submit your information. Please try again.');
    }
  };

  if (loading) {
    return (
      <Container className="seller-valuation-loading">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading your personalized valuation...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="seller-valuation-error">
        <Row className="justify-content-center">
          <Col md={6}>
            <Alert variant="danger">{error}</Alert>
            <Button onClick={() => navigate('/')}>Return Home</Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <div className="seller-valuation-container">
      {/* Simplified header for consumers */}
      {agentInfo && (
        <div className="seller-header">
          <Container>
            <Row className="align-items-center">
              <Col md={8}>
                <div className="agent-branding d-flex align-items-center">
                  {agentInfo.photo && (
                    <img 
                      src={agentInfo.photo} 
                      alt={agentInfo.name}
                      className="agent-photo"
                    />
                  )}
                  <div className="agent-details ms-3">
                    <h4>{agentInfo.name}</h4>
                    {agentInfo.brokerage && <p className="mb-0 text-muted">{agentInfo.brokerage}</p>}
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-end">
                <Button variant="outline-primary" size="sm" href={`tel:${agentInfo.phone}`}>
                  Call {agentInfo.phone}
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      )}
      
      <Container>
        <Row className="justify-content-center">
          <Col lg={10}>
            {!valuationComplete ? (
              <>
                <div className="valuation-intro text-center mb-4">
                  <h1>Your Personalized Home Valuation</h1>
                  <p className="lead">
                    Get an instant, AI-powered estimate of your home's value with detailed market insights
                  </p>
                </div>
                
                {/* Embed Smart CMA Workflow with consumer mode */}
                <div className="workflow-wrapper">
                  <SmartCMAWorkflow 
                    mode="consumer"
                    hideAdvancedOptions={true}
                    onComplete={handleValuationComplete}
                    skipAuthentication={true}
                    autoAdvance={true}
                  />
                </div>
              </>
            ) : (
              /* Lead capture after viewing valuation */
              <Card className="lead-capture-card">
                <Card.Body>
                  <h3>Get Your Personalized Selling Strategy</h3>
                  <p className="mb-4">
                    You've seen your home's estimated value. Now let's create a plan to maximize it.
                  </p>
                  
                  <Row>
                    <Col md={6}>
                      <h5>What you'll receive:</h5>
                      <ul className="benefits-list">
                        <li>📊 Full market analysis report (15+ pages)</li>
                        <li>🏠 Top improvements to maximize value</li>
                        <li>📅 Best time to list analysis</li>
                        <li>💰 Detailed pricing strategy</li>
                        <li>📸 Professional photography consultation</li>
                        <li>📱 Direct access to your agent</li>
                      </ul>
                    </Col>
                    <Col md={6}>
                      <Form onSubmit={handleLeadCapture}>
                        {!leadId && (
                          <>
                            <Form.Group className="mb-3">
                              <Form.Label>Your Name</Form.Label>
                              <Form.Control 
                                required
                                value={captureInfo.name}
                                onChange={(e) => setCaptureInfo({...captureInfo, name: e.target.value})}
                              />
                            </Form.Group>
                            <Form.Group className="mb-3">
                              <Form.Label>Email</Form.Label>
                              <Form.Control 
                                type="email" 
                                required
                                value={captureInfo.email}
                                onChange={(e) => setCaptureInfo({...captureInfo, email: e.target.value})}
                              />
                            </Form.Group>
                            <Form.Group className="mb-3">
                              <Form.Label>Phone</Form.Label>
                              <Form.Control 
                                type="tel" 
                                required
                                value={captureInfo.phone}
                                onChange={(e) => setCaptureInfo({...captureInfo, phone: e.target.value})}
                              />
                            </Form.Group>
                          </>
                        )}
                        <Form.Group className="mb-3">
                          <Form.Label>When are you thinking of selling?</Form.Label>
                          <Form.Select 
                            required
                            value={captureInfo.timeline}
                            onChange={(e) => setCaptureInfo({...captureInfo, timeline: e.target.value})}
                          >
                            <option>ASAP</option>
                            <option>1-3 months</option>
                            <option>3-6 months</option>
                            <option>6-12 months</option>
                            <option>Just exploring</option>
                          </Form.Select>
                        </Form.Group>
                        <Button type="submit" size="lg" className="w-100">
                          Get My Personalized Strategy
                        </Button>
                      </Form>
                      
                      {agentInfo && (
                        <div className="agent-contact-info mt-3 text-center">
                          <small className="text-muted">Or call directly:</small>
                          <p className="mb-0">
                            <a href={`tel:${agentInfo.phone}`}>{agentInfo.phone}</a>
                          </p>
                        </div>
                      )}
                    </Col>
                  </Row>
                  
                  {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SellerValuationWrapper;